import get from 'lodash/get';
import set from 'lodash/set';
import clone from 'lodash/clone';
import filter from 'lodash/filter';
import find from 'lodash/find';
import update from 'immutability-helper';
import { compose, flattenProp } from 'react-recompose';
import GraphQLLoader from '../components/GraphQLLoader';
import GraphQLError from '../components/GraphQLError';

/* istanbul ignore next */
function trimClone(definition, parts) {
  if (parts.length) {
    definition.selectionSet = clone(definition.selectionSet);
    definition.selectionSet.selections = filter(definition.selectionSet.selections, {
      name: { value: parts[0] },
    }).map((selection) => clone(selection));
    trimClone(definition.selectionSet.selections[0], parts.slice(1));
  }
}

/* istanbul ignore next */
function buildSubQuery(query, key) {
  const parts = key.split('.');
  const subQuery = clone(query);
  subQuery.definitions = subQuery.definitions.map((definition) => clone(definition));
  trimClone(subQuery.definitions[0], parts);
  return subQuery;
}

/* istanbul ignore next */
function traverseQuery(definition, parts) {
  if (parts.length) {
    const selection = find(definition.selectionSet.selections, {
      name: { value: parts[0] },
    });
    if (selection) {
      return traverseQuery(selection, parts.slice(1));
    } else {
      const inlineFragment = find(definition.selectionSet.selections, { kind: 'InlineFragment' });
      if (inlineFragment) {
        return traverseQuery(inlineFragment, parts);
      } else {
        return null;
      }
    }
  }
  return definition;
}

export const withSetVariablesProp = ({ data }) => {
  const setVariables = (newVariables) => {
    const variables = { ...data.variables, ...newVariables };
    data.refetch(variables);
  };
  return {
    data,
    setVariables,
  };
};

/* istanbul ignore next */
function getInputName(query, key, name) {
  const parts = key.split('.');
  const selection = traverseQuery(get(query, 'definitions[0]'), parts);
  const argument = find(selection.arguments, { name: { value: name } });
  if (!argument) {
    throw new Error(`Unable to find 'after' argument at ${key}`);
  }
  if (argument.value.kind === 'Variable') {
    return argument.value.name.value;
  } else {
    return argument.value;
  }
}

/* istanbul ignore next */
export const loadMorePaginationProp = (query, { optimized } = {}) => ({ data }) => {
  /* istanbul ignore next */
  const { loading, variables, error, fetchMore } = data;
  /* istanbul ignore next */
  return {
    loading,
    error,
    data,
    loadMore: (collectionPath, extraVariables) => {
      const cursor = get(data, `${collectionPath}.pageInfo.endCursor`);
      if (!cursor) {
        throw new Error(
          `Unable to find cursor information at ${collectionPath}.pageInfo.endCursor`,
        );
      }
      return fetchMore({
        query: optimized ? buildSubQuery(query, collectionPath) : query,
        variables: { ...variables, ...extraVariables, [getInputName(query, collectionPath, 'after')]: cursor },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = get(fetchMoreResult, `${collectionPath}.edges`);
          const pageInfo = get(fetchMoreResult, `${collectionPath}.pageInfo`);
          return update(
            previousResult,
            set({}, collectionPath, {
              pageInfo: { $set: pageInfo },
              edges: { $push: newEdges },
            }),
          );
        },
      });
    },
  };
};

export const SimpleLoadingComponent = (graphqlData, ...additional) =>
  compose(graphqlData, GraphQLLoader, GraphQLError, flattenProp('data'), ...additional);
export const SimpleGraphQLComponent = (graphqlData, ...additional) =>
  compose(graphqlData, GraphQLError, flattenProp('data'), ...additional);
